import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

export const Button = styled.button<{ active?: boolean }>`
  @import 'stylesheets/design_system/breakpoint';

  border: 1px solid #ffffff;
  border-radius: 18px;
  background-color: #000;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 32px;
  padding: 16px;
  position: relative;

  &.active,
  &:hover:not(&:disabled) {
    background-color: #ffffff;
    color: #000000;
  }

  &:disabled {
    color: #aaa;
    cursor: auto;
  }

  @include breakpoint(small) {
    width: 100%;
    height: 100%;
  }
`;

export const Label = styled.p<{
  bigMargin?: boolean;
  bold?: boolean;
  mono?: boolean;
}>`
  color: #fff;
  line-height: 1;
  margin: 0 8px 0 0;

  // Overrides some profile specific CSS
  [class^='c-profiles'] &:hover,
  &:hover {
    color: #fff;
  }

  &.bigMargin {
    margin: 0 20px 0 0;
  }

  &.bold {
    line-height: 16px;
    font-weight: 500;
  }

  &.mono {
    font-family: 'Roboto Mono', monospace;
    letter-spacing: 0.05em;
  }
`;

export const MenuContainer = styled.div<{
  wide?: boolean;
  widest?: boolean;
  right?: boolean;
  docked?: boolean;
}>`
  background: #000;
  bottom: 79px;
  color: #fff;
  display: flex;
  flex-direction: column;
  font-family: 'Roboto Mono', monospace;
  left: 0;
  line-height: 1.4;
  list-style: none;
  max-height: 70vh;
  min-width: 420px;
  overflow: auto;
  position: absolute;
  text-align: left;

  &:hover {
    cursor: auto;
  }

  &.wide {
    width: 520px;
  }

  &.widest {
    width: 600px;
  }

  &.right {
    left: auto;
    right: 0;
  }

  &.docked {
    max-width: 100vw;
    min-width: 0;
    position: static;
  }
`;

export const MenuList = styled.ul`
  flex: 0 1 auto;
  margin: 0;
  overflow: auto;
  padding: 0;
`;

export const Item = styled.li<{ noClick?: boolean }>`
  align-items: center;
  display: flex;
  gap: 4px;
  padding: 5px 7px;
  white-space: pre;

  &:hover:not(&.noClick),
  &:hover:nth-of-type(odd):not(&.noClick) {
    background: #333;
    cursor: pointer;
  }

  &:nth-of-type(odd) {
    background: #111;
  }

  > a {
    color: #fff;
  }
`;

export const SearchContainer = styled.div`
  align-items: flex-end;
  display: flex;
  padding: 14px;
`;

export const Input = styled.input`
  background: transparent;
  border: 0;
  border-bottom: 1px solid #eee;
  margin-left: 7px;
  width: 100%;
  color: #ccc;
`;

export const Container = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  background-color: #000000;
  z-index: 1000;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  color: #ffffff;
  font-size: 14px;
  padding: 8px;

  @include breakpoint(small) {
    height: 100%;
    overflow-y: scroll;
  }
`;

export const HideButtonContainer = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;

  @include breakpoint(medium, large) {
    position: absolute;
    top: -48px;
    right: 0px;
  }
`;

export const HideButton = styled.button`
  width: 48px;
  height: 48px;
  cursor: pointer;
  background-color: #000000;
  color: #ffffff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #333333;
  }
`;

export const ButtonMenuContainer = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;

  @include breakpoint(small) {
    width: 100%;
  }
`;

export const ActivatorButtonContainer = styled.button`
  position: fixed;
  width: 64px;
  height: 64px;
  bottom: 14px;
  left: 14px;
  z-index: 2147483000;
  cursor: pointer;
  overflow: hidden;
  color: #fff;
  background-color: #000;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.33);
  border: none;
  border-radius: 50%;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: 0.3;
  transition: opacity 0.14s ease-in-out;

  &:hover {
    opacity: 1;
    transition: opacity 0.24s ease-in-out;
  }
`;

const LoadingButtonContainer = styled(ActivatorButtonContainer)`
  cursor: not-allowed;
  background-color: #333;
  opacity: 0.8;

  &:hover {
    opacity: 0.8;
  }
`;

export const ActivatorButton: React.FC<
  React.ComponentPropsWithoutRef<'button'>
> = (props) => (
  <ActivatorButtonContainer {...props}>
    <Icon name="admin_panel_settings" size={24} />
  </ActivatorButtonContainer>
);

export const LoadingButton: React.FC = () => (
  <LoadingButtonContainer disabled title="Loading Admin Panel...">
    <Icon name="hourglass_empty" size={24} />
  </LoadingButtonContainer>
);
